import FontAwesomeIcon from "~/components/FontAwesome";
import {Link} from "react-router-dom";
import {useCan, useIsAdmin} from "~/hooks";

/**
 * Danh sách menu điều hướng CHẾ ĐỘ NGƯỜI DÙNG, chia section (type:'group'):
 * TỔNG QUAN · CÔNG CỤ SEO · DỰ ÁN · TÀI KHOẢN.
 *
 * Module nghiệp vụ mới tự thêm mục (kèm kiểm tra quyền qua useCan) vào section
 * phù hợp. Menu quản trị nằm riêng ở AdminNavData (AdminSidebar).
 *
 * NavBarData được gọi trong render của Sidebar nên dùng hook useCan ở đây hợp lệ.
 */
export const NavBarData = () => {

	const canSeo = useCan('seo');
	const canPageSpeed = useCan('pagespeed');
	const canTrends = useCan('trends');
	const canSchema = useCan('schema');
	const canProject = useCan('project');
	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canAi = useCan('ai');
	const canMember = useCan('member_manage');
	const isAdmin = useIsAdmin();

	// ===== TỔNG QUAN =====
	const overview = [
		{
			key: 'home',
			label: <Link to="/">Dashboard</Link>,
			title: 'Dashboard',
			to: '/',
			icon: <FontAwesomeIcon icon="fa-light fa-house" />,
			count: 0,
		},
		// Thuật ngữ SEO — từ điển giải thích cho người mới (không cần quyền).
		{
			key: 'glossary',
			label: <Link to="/glossary">Thuật ngữ SEO</Link>,
			title: 'Thuật ngữ SEO — giải thích cho người mới',
			to: '/glossary',
			icon: <FontAwesomeIcon icon="fa-light fa-book-open-cover" />,
			count: 0,
		},
	];

	// ===== CÔNG CỤ SEO =====
	const tools = [];

	// Chấm điểm SEO (cần quyền 'seo'; root bypass).
	if (canSeo) {
		tools.push({
			key: 'seo',
			label: 'Chấm điểm SEO',
			title: 'Chấm điểm SEO',
			to: '/seo',
			icon: <FontAwesomeIcon icon="fa-light fa-magnifying-glass-chart" />,
			children: [
				{
					key: 'seo-check',
					label: <Link to="/seo">Chấm điểm mới</Link>,
					title: 'Chấm điểm mới',
					to: '/seo',
				},
				{
					key: 'seo-history',
					label: <Link to="/seo/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/seo/history',
				},
			],
		});
	}

	// PageSpeed / Core Web Vitals (cần quyền 'pagespeed'; root bypass).
	if (canPageSpeed) {
		tools.push({
			key: 'pagespeed',
			label: 'PageSpeed / CWV',
			title: 'PageSpeed / Core Web Vitals',
			to: '/pagespeed',
			icon: <FontAwesomeIcon icon="fa-light fa-gauge-high" />,
			children: [
				{
					key: 'pagespeed-check',
					label: <Link to="/pagespeed">Kiểm tra mới</Link>,
					title: 'Kiểm tra mới',
					to: '/pagespeed',
				},
				{
					key: 'pagespeed-history',
					label: <Link to="/pagespeed/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/pagespeed/history',
				},
			],
		});
	}

	// Google Trends (cần quyền 'trends'; root bypass). Lịch sử là tab trong trang.
	if (canTrends) {
		tools.push({
			key: 'trends',
			label: <Link to="/trends">Google Trends</Link>,
			title: 'Google Trends — xu hướng tìm kiếm',
			to: '/trends',
			icon: <FontAwesomeIcon icon="fa-light fa-arrow-trend-up" />,
		});
	}

	// Schema.org Generator + Validator (cần quyền 'schema'; root bypass).
	if (canSchema) {
		tools.push({
			key: 'schema',
			label: 'Schema.org',
			title: 'Schema.org Generator/Validator',
			to: '/schema/generator',
			icon: <FontAwesomeIcon icon="fa-light fa-shapes" />,
			children: [
				{
					key: 'schema-generator',
					label: <Link to="/schema/generator">Tạo schema</Link>,
					title: 'Tạo schema',
					to: '/schema/generator',
				},
				{
					key: 'schema-validator',
					label: <Link to="/schema/validator">Kiểm tra schema</Link>,
					title: 'Kiểm tra schema',
					to: '/schema/validator',
				},
				{
					key: 'schema-saved',
					label: <Link to="/schema/saved">Đã lưu</Link>,
					title: 'Đã lưu',
					to: '/schema/saved',
				},
			],
		});
	}

	// AI SEO (cần quyền 'ai'; root bypass).
	if (canAi) {
		tools.push({
			key: 'ai',
			label: 'AI SEO',
			title: 'AI SEO — sinh nội dung',
			to: '/ai',
			icon: <FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />,
			children: [
				{
					key: 'ai-create',
					label: <Link to="/ai">Tạo nội dung</Link>,
					title: 'Tạo nội dung',
					to: '/ai',
				},
				{
					key: 'ai-history',
					label: <Link to="/ai/history">Lịch sử</Link>,
					title: 'Lịch sử',
					to: '/ai/history',
				},
			],
		});
	}

	// ===== DỰ ÁN =====
	const projects = [];

	// Dự án (thực thể tổng chứa các tool SEO). Hiện khi có quyền quản lý dự án
	// hoặc có quyền bất kỳ tool nào bên trong (vd Site Crawler). root bypass.
	if (canProject || canCrawl || canGsc || canAi) {
		projects.push({
			key: 'projects',
			label: <Link to="/projects">Dự án</Link>,
			title: 'Dự án',
			to: '/projects',
			icon: <FontAwesomeIcon icon="fa-light fa-folder-tree" />,
			count: 0,
		});
	}

	// ===== TÀI KHOẢN =====
	const account = [
		// Gói & Hạn mức — mọi user đều xem được gói của chính mình (không cần quyền riêng).
		{
			key: 'subscription',
			label: <Link to="/subscription">Gói & Hạn mức</Link>,
			title: 'Gói & Hạn mức',
			to: '/subscription',
			icon: <FontAwesomeIcon icon="fa-light fa-crown" />,
			count: 0,
		},
		// API & Webhook (#25) — mọi user xem được tài liệu; tạo key/webhook gate theo gói.
		{
			key: 'api-access',
			label: <Link to="/api-access">API & Webhook</Link>,
			title: 'API & Webhook',
			to: '/api-access',
			icon: <FontAwesomeIcon icon="fa-light fa-code" />,
			count: 0,
		},
		{
			key: 'profile',
			label: <Link to="/account">Hồ sơ cá nhân</Link>,
			title: 'Hồ sơ cá nhân',
			to: '/account',
			icon: <FontAwesomeIcon icon="fa-light fa-id-card" />,
			count: 0,
		},
	];

	// Cửa sang CHẾ ĐỘ QUẢN TRỊ — chỉ hiện với quản trị viên (menu quản trị
	// nằm riêng trong AdminLayout, không trộn vào menu công cụ của user).
	if (isAdmin) {
		account.push({
			key: 'goto-admin',
			label: <Link to={canMember ? '/admin/stats' : '/admin/permission'}>Quản trị hệ thống</Link>,
			title: 'Quản trị hệ thống',
			to: '/admin',
			icon: <FontAwesomeIcon icon="fa-light fa-shield-halved" />,
			className: 'menu-admin-link',
		});
	}

	// Ghép section — bỏ section rỗng (user không có quyền nào trong nhóm).
	const sections = [
		{key: 'g-overview', label: 'Tổng quan', children: overview},
		{key: 'g-tools', label: 'Công cụ SEO', children: tools},
		{key: 'g-projects', label: 'Dự án', children: projects},
		{key: 'g-account', label: 'Tài khoản', children: account},
	];

	return sections
		.filter((s) => s.children.length > 0)
		.map((s) => ({type: 'group', ...s}));
}
